/**
 * Module-pane persistence/hydration + module-catalog helpers.
 *
 * Each helper takes an explicit deps object so the in-component useCallback
 * shells keep the same dep arrays. The inflight race-guard semantics
 * (panelCreateInflightRef, moduleAttachInFlightRef) must be preserved exactly.
 */
import type { MutableRefObject } from 'react';
import { api } from '@/lib/api';
import type { ConversationPanel, WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import { isSummaryModuleType, type Module } from '@/types/modules';
import { isRecord } from '@/features/chat/utils/chat-text';
import {
  parseChatPanePanelState,
  getChatPaneStateTimestamp,
} from '@/features/chat/utils/chat-pane-state';
import { toChatModuleFromWorkspaceModule } from '@/features/chat/utils/workspace-module-mapping';

type LoadConversationPanels = (
  conversationId: string,
  options?: { forceRefresh?: boolean },
) => Promise<ConversationPanel[]>;

type UpsertConversationPanelCache = (
  conversationId: string,
  panel: ConversationPanel,
) => void;

export interface ResolveChatPaneModuleParams {
  /** The module the pane is CURRENTLY targeting (user intent / URL / hydration). */
  chatPaneModuleId: string | null;
  /** Modules attached to the active conversation (the only ones we can render). */
  availableModules: Module[];
  /** Whether the pane is intended visible. */
  isPaneVisible: boolean;
  /** The last module the pane actually rendered (a REAL resolution, never a bridge). */
  lastRenderedModule: Module | null;
}

export interface ResolveChatPaneModuleResult {
  /**
   * The REAL resolution — the targeted module IFF it exists in
   * `availableModules`. Source of truth for cleanup (module-pane-missing-clear);
   * must never be bridged/masked or a genuinely-deleted module is never closed.
   */
  resolved: Module | null;
  /**
   * What to RENDER. Equals `resolved` when the target is present; otherwise
   * BRIDGES to the last rendered module while the pane is visible and the target
   * hasn't attached yet.
   */
  rendered: Module | null;
}

/**
 * Resolve which module the chat pane should render, keeping the split grid
 * stable during the window where the targeted module isn't in
 * `availableModules` yet — WITHOUT ever painting a module that doesn't belong to
 * the current context.
 *
 * There are exactly two windows where the target is legitimately absent but the
 * pane should stay open on the previous module:
 *
 *   (a) LOADING — the active conversation's module list hasn't arrived yet
 *       (`availableModules` empty) and we're still targeting the SAME module we
 *       were showing. Keep it up so a conversation re-open doesn't flash
 *       chat-only.
 *   (b) WITHIN-CONVERSATION SWITCH — the user clicked a different module whose
 *       panel hasn't attached yet, so `availableModules` still holds the
 *       PREVIOUS module. Keep the previous module (still present in the current
 *       `availableModules`) on screen so the open View Transition morphs into
 *       the new module instead of snapshotting an empty grid and slamming the
 *       whole panel shut then reopening.
 *
 * The bridge is gated on the last rendered module being either the same target
 * (a) or STILL present in `availableModules` (b). That gate is what keeps two
 * other transitions honest:
 *
 *   - CONVERSATION SWITCH — once `availableModules` has SETTLED to the NEW
 *     conversation's set it no longer contains the previous module, so we do
 *     NOT bridge and the pane collapses rather than painting the old
 *     conversation's module in the new conversation. The swap is kept atomic by
 *     `buildAvailableModules` (below), which drops any lingering panel-bound
 *     module whose `conversation_id` doesn't match the active conversation — so
 *     even though `Module.id` is the shared workspace-module id and the prior
 *     conversation's `panelBoundModules` linger until their async refresh lands,
 *     `availableModules` never mixes two conversations and neither this
 *     membership gate nor the `resolved` path above can match the previous
 *     conversation's module during the switch (#5188).
 *   - MODULE DELETED WHILE OPEN — the module leaves `availableModules`, so we do
 *     NOT bridge; the pane collapses immediately (and runModulePaneMissingClear
 *     then closes it) instead of rendering the deleted module — possibly with
 *     404-ing data — for the whole grace window.
 *
 * `resolved` is exposed separately so cleanup validates against reality, never
 * the bridged value.
 */
export function resolveChatPaneModule(
  params: ResolveChatPaneModuleParams,
): ResolveChatPaneModuleResult {
  const { chatPaneModuleId, availableModules, isPaneVisible, lastRenderedModule } = params;
  if (!chatPaneModuleId) return { resolved: null, rendered: null };
  const resolved = availableModules.find((module) => module.id === chatPaneModuleId) || null;
  if (resolved) return { resolved, rendered: resolved };
  if (isPaneVisible && lastRenderedModule) {
    // (a) still loading this conversation's modules, same target as before
    const loadingSameTarget = availableModules.length === 0
      && lastRenderedModule.id === chatPaneModuleId;
    // (b) switching within a loaded conversation — the previous module is still
    //     attached (present in availableModules) while the new target attaches
    const previousStillAttached = availableModules.some(
      (module) => module.id === lastRenderedModule.id,
    );
    if (loadingSameTarget || previousStillAttached) {
      return { resolved: null, rendered: lastRenderedModule };
    }
  }
  return { resolved: null, rendered: null };
}

/**
 * Build the module list the chat pane can render for the active conversation:
 * the conversation's own modules (`baseConversationModules`) merged with the
 * panel-bound modules, deduped by id (freshest/panel-bound record wins), ordered
 * by position, with archived/summary modules dropped.
 *
 * Panel-bound modules are conversation-scoped — `toChatModuleFromWorkspaceModule`
 * tags each with the `conversation_id` it was fetched for. On an A→B switch
 * `baseConversationModules` flips to B synchronously while `panelBoundModules`
 * linger on A until their async refresh lands; because `Module.id` is the shared
 * workspace-module id, a lingering A panel could otherwise be matched (and
 * painted) as if it belonged to B — the transient conversation mix #5188 targets.
 * Filtering panel-bound modules by `conversation_id` drops those stale entries so
 * `availableModules` never mixes two conversations, for the direct `resolved`
 * match and the bridge path alike. Base modules are always the active
 * conversation's own set, so they are never filtered.
 */
export function buildAvailableModules(
  baseConversationModules: Module[],
  panelBoundModules: Module[],
  activeConversationId: string | null,
): Module[] {
  const currentPanelBoundModules = activeConversationId
    ? panelBoundModules.filter((module) => module.conversation_id === activeConversationId)
    : [];
  const mergedModules = [...baseConversationModules, ...currentPanelBoundModules];
  const dedupedById = new Map<string, Module>();
  for (const module of mergedModules) {
    if (!module || module.archived || isSummaryModuleType(module.type)) continue;
    // Prefer the freshest record for a module id (panel-bound modules come last).
    dedupedById.set(module.id, module);
  }
  return Array.from(dedupedById.values()).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

export interface PersistModulePaneStateDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
  upsertConversationPanelCache: UpsertConversationPanelCache;
  panelCreateInflightRef: MutableRefObject<Set<string>>;
  conversationPanelsCacheRef: MutableRefObject<Record<string, ConversationPanel[]>>;
}

export async function persistModulePaneState(
  conversationId: string,
  moduleId: string,
  paneState: { isVisible: boolean; isFullscreen: boolean; anchorMessageId?: string | null },
  deps: PersistModulePaneStateDeps,
): Promise<void> {
  const {
    token,
    workspaceId,
    loadConversationPanels,
    upsertConversationPanelCache,
    panelCreateInflightRef,
    conversationPanelsCacheRef,
  } = deps;
  if (!token || !workspaceId || !conversationId || !moduleId) return;

  try {
    let panels = await loadConversationPanels(conversationId);
    let panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;

    if (!panel) {
      const inflightKey = `${conversationId}:${moduleId}`;
      if (panelCreateInflightRef.current.has(inflightKey)) {
        // Another call is already creating this panel — wait and re-fetch
        await new Promise<void>((r) => setTimeout(r, 500));
        panels = await loadConversationPanels(conversationId, { forceRefresh: true });
        panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        if (!panel) return;
      } else {
        panelCreateInflightRef.current.add(inflightKey);
        try {
          const createdPanel = await api.workspaces.createConversationPanel(
            token,
            workspaceId,
            conversationId,
            {
              workspaceModuleId: moduleId,
              panelKey: `module:${moduleId}`,
            },
          );
          upsertConversationPanelCache(conversationId, createdPanel);
          panels = conversationPanelsCacheRef.current[conversationId] || panels;
          panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        } catch (createError: any) {
          // 409 means the panel already exists — just re-fetch and continue
          if (createError?.status !== 409) {
            console.warn('[ChatBox] Failed to create panel for module pane state persistence', {
              conversationId,
              moduleId,
              createError,
            });
          }
          panels = await loadConversationPanels(conversationId, { forceRefresh: true });
          panel = panels.find((entry) => !entry.archivedAt && entry.moduleId === moduleId) ?? null;
        } finally {
          panelCreateInflightRef.current.delete(inflightKey);
        }
      }
    }

    if (!panel) return;

    const currentPanelState = isRecord(panel.state) ? panel.state : {};
    const currentChatPaneState = parseChatPanePanelState(currentPanelState.chatPane);
    const nextAnchorMessageId = paneState.anchorMessageId !== undefined
      ? paneState.anchorMessageId
      : (currentChatPaneState?.anchorMessageId ?? null);
    const nextState = {
      ...currentPanelState,
      chatPane: {
        open: paneState.isVisible,
        fullscreen: paneState.isFullscreen,
        anchorMessageId: nextAnchorMessageId,
        updatedAt: new Date().toISOString(),
      },
    };
    const updatedPanel = await api.workspaces.updateConversationPanel(
      token,
      workspaceId,
      conversationId,
      panel.id,
      { state: nextState },
    );
    upsertConversationPanelCache(conversationId, updatedPanel);
  } catch (error) {
    console.warn('[ChatBox] Failed to persist module pane state', {
      conversationId,
      moduleId,
      error,
    });
  }
}

export interface HydrateModulePaneStateDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
}

export async function hydrateModulePaneState(
  conversationId: string,
  deps: HydrateModulePaneStateDeps,
): Promise<{
  moduleId: string;
  isVisible: boolean;
  isFullscreen: boolean;
  anchorMessageId: string | null;
} | null> {
  const { token, workspaceId, loadConversationPanels } = deps;
  if (!token || !workspaceId || !conversationId) return null;

  try {
    const panels = await loadConversationPanels(conversationId, { forceRefresh: true });
    const candidates = panels
      .filter((panel) => !panel.archivedAt)
      .map((panel) => ({
        panel,
        chatPane: parseChatPanePanelState(isRecord(panel.state) ? panel.state.chatPane : undefined),
      }))
      .filter((entry) => Boolean(entry.chatPane));

    if (candidates.length === 0) return null;

    candidates.sort((left, right) => (
      getChatPaneStateTimestamp(right.chatPane, right.panel.updatedAt)
      - getChatPaneStateTimestamp(left.chatPane, left.panel.updatedAt)
    ));

    const latest = candidates[0];
    if (!latest?.panel?.moduleId || !latest.chatPane) return null;

    return {
      moduleId: latest.panel.moduleId,
      isVisible: latest.chatPane.open,
      isFullscreen: latest.chatPane.fullscreen,
      anchorMessageId: latest.chatPane.anchorMessageId ?? null,
    };
  } catch (error) {
    console.warn('[ChatBox] Failed to hydrate module pane state', { conversationId, error });
    return null;
  }
}

export interface EnsureModulePanelInConversationDeps {
  token: string | null;
  workspaceId: string | null;
  loadConversationPanels: LoadConversationPanels;
  upsertConversationPanelCache: UpsertConversationPanelCache;
}

export async function ensureModulePanelInConversation(
  conversationId: string,
  moduleId: string,
  deps: EnsureModulePanelInConversationDeps,
): Promise<boolean> {
  const { token, workspaceId, loadConversationPanels, upsertConversationPanelCache } = deps;
  if (!token || !workspaceId) return false;

  try {
    const panels = await loadConversationPanels(conversationId, { forceRefresh: true });
    const alreadyAttached = panels.some((panel) => !panel.archivedAt && panel.moduleId === moduleId);
    if (alreadyAttached) return true;

    // An ARCHIVED panel for this module must be UN-archived, not re-created:
    // the server's create is an ON CONFLICT (conversation_id, panel_key)
    // upsert that only bumps updated_at, so creating over an archived panel
    // silently returns the still-archived row. The module then never enters
    // availableModules (refreshPanelBoundModules filters archived panels),
    // the focus retries exhaust, the 1800ms missing-module cleanup closes
    // the pane, and the URL guard redirects to the remembered module — the
    // click lands on a STALE module (#5266). Mirrors the sidebar twins
    // (useModuleLaunch/SidebarRail ensureModulePanelAttached).
    const archivedPanel = panels.find((panel) => panel.moduleId === moduleId);
    if (archivedPanel) {
      const restoredPanel = await api.workspaces.updateConversationPanel(
        token,
        workspaceId,
        conversationId,
        archivedPanel.id,
        { archived: false },
      );
      upsertConversationPanelCache(conversationId, restoredPanel);
      return true;
    }

    const workspaceModules = await api.workspaces.listWorkspaceModules(token, workspaceId, {
      includeArchived: true,
    });
    const moduleExists = workspaceModules.some((module) => module.id === moduleId && !module.archivedAt);
    if (!moduleExists) return false;

    const createdPanel = await api.workspaces.createConversationPanel(
      token,
      workspaceId,
      conversationId,
      {
        workspaceModuleId: moduleId,
        panelKey: `module:${moduleId}`,
      },
    );
    upsertConversationPanelCache(conversationId, createdPanel);
    return true;
  } catch (error) {
    console.warn('[ChatBox] Failed to attach module panel to conversation', {
      conversationId,
      moduleId,
      error,
    });
    return false;
  }
}

export interface EnsureModulePanelInConversationOnceDeps {
  moduleAttachInFlightRef: MutableRefObject<Map<string, Promise<boolean>>>;
  ensureModulePanelInConversation: (
    conversationId: string,
    moduleId: string,
  ) => Promise<boolean>;
}

export async function ensureModulePanelInConversationOnce(
  conversationId: string,
  moduleId: string,
  deps: EnsureModulePanelInConversationOnceDeps,
): Promise<boolean> {
  const { moduleAttachInFlightRef, ensureModulePanelInConversation } = deps;
  const key = `${conversationId}:${moduleId}`;
  const existing = moduleAttachInFlightRef.current.get(key);
  if (existing) return existing;

  const pending = ensureModulePanelInConversation(conversationId, moduleId)
    .finally(() => {
      moduleAttachInFlightRef.current.delete(key);
    });

  moduleAttachInFlightRef.current.set(key, pending);
  return pending;
}

export interface RefreshWorkspaceModuleCatalogDeps {
  token: string | null;
  workspaceId: string | null;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  setWorkspaceModuleLabels: (labels: string[]) => void;
}

export async function refreshWorkspaceModuleCatalog(
  deps: RefreshWorkspaceModuleCatalogDeps,
): Promise<WorkspaceModule[]> {
  const { token, workspaceId, workspaceModuleCatalogRef, setWorkspaceModuleLabels } = deps;
  if (!token || !workspaceId) {
    workspaceModuleCatalogRef.current = [];
    setWorkspaceModuleLabels([]);
    return [];
  }

  try {
    const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {
      includeArchived: false,
    });
    workspaceModuleCatalogRef.current = modules;
    const labels = Array.from(
      new Set(
        modules
          .map((module) => (typeof module.label === 'string' ? module.label.trim() : ''))
          .filter((label) => label.length > 0),
      ),
    );
    setWorkspaceModuleLabels(labels);
    return modules;
  } catch (error) {
    console.warn('[ChatBox] Failed to refresh workspace module catalog', error);
    return workspaceModuleCatalogRef.current;
  }
}

export interface LoadConversationPanelsDeps {
  token: string | null;
  workspaceId: string | null;
  conversationPanelsCacheRef: MutableRefObject<Record<string, ConversationPanel[]>>;
}

export async function loadConversationPanels(
  conversationId: string,
  options: { forceRefresh?: boolean } | undefined,
  deps: LoadConversationPanelsDeps,
): Promise<ConversationPanel[]> {
  const { token, workspaceId, conversationPanelsCacheRef } = deps;
  if (!token || !workspaceId || conversationId.startsWith('conversation-')) return [];

  const cachedPanels = conversationPanelsCacheRef.current[conversationId];
  if (cachedPanels && !options?.forceRefresh) return cachedPanels;

  const panels = await api.workspaces.listConversationPanels(
    token,
    workspaceId,
    conversationId,
    { includeArchived: true },
  );
  conversationPanelsCacheRef.current[conversationId] = panels;
  return panels;
}

export interface RefreshPanelBoundModulesDeps {
  loadConversationPanels: LoadConversationPanels;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  activeConversationIdRef: MutableRefObject<string | null>;
  setPanelBoundModules: (modules: Module[]) => void;
}

export async function refreshPanelBoundModules(
  conversationId: string,
  options: { forceRefresh?: boolean } | undefined,
  deps: RefreshPanelBoundModulesDeps,
): Promise<Module[]> {
  const {
    loadConversationPanels,
    refreshWorkspaceModuleCatalog,
    workspaceModuleCatalogRef,
    activeConversationIdRef,
    setPanelBoundModules,
  } = deps;
  if (!conversationId) return [];

  try {
    const panels = await loadConversationPanels(conversationId, {
      forceRefresh: options?.forceRefresh,
    });
    const panelModuleIds = Array.from(
      new Set(
        panels
          .filter((panel) => !panel.archivedAt && typeof panel.moduleId === 'string' && panel.moduleId.trim())
          .map((panel) => panel.moduleId),
      ),
    );
    if (panelModuleIds.length === 0) {
      if (activeConversationIdRef.current === conversationId) {
        setPanelBoundModules([]);
      }
      return [];
    }

    let workspaceModules = workspaceModuleCatalogRef.current;
    if (options?.forceRefresh || workspaceModules.length === 0) {
      workspaceModules = await refreshWorkspaceModuleCatalog();
    }
    const workspaceModuleById = new Map(
      workspaceModules
        .filter((module) => !module.archivedAt)
        .map((module) => [module.id, module]),
    );
    const resolvedModules = panelModuleIds
      .map((moduleId, index) => {
        const workspaceModule = workspaceModuleById.get(moduleId);
        if (!workspaceModule) return null;
        return toChatModuleFromWorkspaceModule(workspaceModule, conversationId, index + 1);
      })
      .filter((module): module is Module => Boolean(module));

    if (activeConversationIdRef.current === conversationId) {
      setPanelBoundModules(resolvedModules);
    }
    return resolvedModules;
  } catch (error) {
    console.warn('[ChatBox] Failed to refresh panel-bound modules', { conversationId, error });
    return [];
  }
}

// ---------------------------------------------------------------------------
// Module-pane title override — owning-module gating (#5581)
// ---------------------------------------------------------------------------
//
// `ChatModulePane` keeps a single, un-keyed title-override slot that outlives a
// module→module switch (the pane itself is never remounted — only
// `EditableModuleTitle`/`ModuleRenderer` are keyed by module id). The only
// producer is `ChannelsModule`, which sets `'Direct Messages'` while a DM is
// open and clears it in a passive effect. Since #5483 the module→module switch
// bypasses the flushSync-wrapped View Transition, so that corrective clear runs
// as an ordinary deferred effect — late enough that a stale override set for the
// module you're LEAVING can paint for one frame on the module you're switching
// TO. Tagging the override with the id of the producing module, and only
// surfacing it while that exact module is showing, closes the gap regardless of
// effect timing. Both helpers are pure so the gate is unit-testable.

export interface ModuleTitleOverride {
  moduleId: string;
  title: string;
}

/** Shape of the `xavior:module-title-override` CustomEvent `detail`. */
export interface ModuleTitleOverrideEvent {
  moduleId?: string;
  title?: string | null;
}

/**
 * Reduce an incoming override event against the current override slot.
 *
 * A tagged, non-empty title becomes the new override. An empty/null title is a
 * clear: it drops only the override owned by the emitting module (scoped clear),
 * or every override when the event carries no owner (legacy/global clear) — so a
 * late clear from a module you already left can never wipe a fresh override
 * belonging to the module now on screen.
 */
export function nextModuleTitleOverride(
  prev: ModuleTitleOverride | null,
  detail: ModuleTitleOverrideEvent | undefined,
): ModuleTitleOverride | null {
  const title = detail?.title ?? null;
  const moduleId = detail?.moduleId;
  if (title && moduleId) return { moduleId, title };
  if (prev && moduleId && prev.moduleId !== moduleId) return prev;
  return null;
}

/**
 * Label to hand `EditableModuleTitle`, or null when the override belongs to a
 * different module than the one currently in the pane (the #5581 bleed guard).
 */
export function resolveModuleTitleOverride(
  override: ModuleTitleOverride | null,
  currentModuleId: string,
): string | null {
  if (!override) return null;
  return override.moduleId === currentModuleId ? override.title : null;
}
